import { apiTransport } from './api-client';
import type { CompanySettings } from './modules';

/**
 * Organisation settings, against the API.
 *
 * Every section is the server's. The company profile lives on the
 * organisation; the attendance policy, employee numbering, approval rules and
 * idle timeout live on the organisation's settings row, and each of them is
 * applied by the server (or, for the idle timeout, by every signed-in
 * browser) rather than merely recorded.
 *
 * Saving carries a reason, because these are the settings whose audit entry
 * gets read: "who halved the geofence radius in March and why" is a question
 * with a payroll dispute behind it. Enabling continuous tracking carries an
 * acknowledgement as well — the server refuses it otherwise, by design.
 */

export interface ApiOrganizationProfile {
  legalName: string;
  displayName: string;
  industry: string | null;
  contactEmail: string | null;
  contactNumber: string | null;
  registeredAddress: string | null;
  timezone: string;
  country: string;
  currency: string;
}

export interface ApiOrganizationSettings {
  graceMinutes: number;
  standardWorkHours: number;
  requireSelfieOnTimeIn: boolean;
  requireLocationOnTimeIn: boolean;
  requireLocationOnBreak: boolean;
  geofenceRadiusMeters: number;
  minimumGpsAccuracyMeters: number;
  maximumOfflineDelayHours: number;
  continuousLocationTracking: boolean;
  attendanceReviewThreshold: number;
  attendanceRejectThreshold: number;
  payslipVisibilityDays: number;
  attendanceSelfieRetentionDays: number;
  attendanceLocationRetentionDays: number;
  employeeNumberPrefix: string;
  employeeNumberPadding: number;
  employeeNumberIncludeYear: boolean;
  leaveRequiresHrApproval: boolean;
  overtimeRequiresHrApproval: boolean;
  correctionRequiresHrApproval: boolean;
  compensationRequiresApproval: boolean;
  sessionIdleTimeoutMinutes: number;
  /** The next value of each employee counter, for the numbering preview. */
  employeeNumberNext: { year: number; yearly: number; continuous: number };
  updatedAt: string;
}

/*
 * Screen keys to server keys, per section. The screen's names predate the
 * server fields; mapping here keeps both readable rather than renaming either.
 */
const SECTION_FIELDS = {
  employeeNumbering: {
    prefix: 'employeeNumberPrefix',
    padding: 'employeeNumberPadding',
    includeYear: 'employeeNumberIncludeYear',
  },
  approvals: {
    leaveRequiresHr: 'leaveRequiresHrApproval',
    overtimeRequiresHr: 'overtimeRequiresHrApproval',
    correctionRequiresHr: 'correctionRequiresHrApproval',
    compensationRequiresApproval: 'compensationRequiresApproval',
  },
  session: { idleTimeoutMinutes: 'sessionIdleTimeoutMinutes' },
} as const satisfies Record<string, Record<string, keyof ApiOrganizationSettings>>;

export type ServerSection = keyof typeof SECTION_FIELDS;

export function isServerSection(section: string): section is ServerSection {
  return section in SECTION_FIELDS;
}

/** One section, in the screen's field names. */
export function toSection(
  settings: ApiOrganizationSettings,
  section: ServerSection,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(SECTION_FIELDS[section]).map(([screen, server]) => [screen, settings[server]]),
  );
}

export const apiSettingsService = {
  async profile(): Promise<ApiOrganizationProfile> {
    return apiTransport().get<ApiOrganizationProfile>('/api/v1/organization/profile');
  },

  async attendancePolicy(): Promise<ApiOrganizationSettings> {
    return apiTransport().get<ApiOrganizationSettings>('/api/v1/organization/settings');
  },

  /** The company section, in the screen\'s field names. */
  toCompanySection(profile: ApiOrganizationProfile): Record<string, string> {
    return {
      legalName: profile.legalName,
      tradeName: profile.displayName,
      industry: profile.industry ?? '',
      contactEmail: profile.contactEmail ?? '',
      contactNumber: profile.contactNumber ?? '',
      timezone: profile.timezone,
      address: profile.registeredAddress ?? '',
    };
  },

  async saveCompany(values: Record<string, unknown>, reason: string) {
    return apiTransport().patch('/api/v1/organization/profile', {
      ...companyBody(values),
      reason,
    });
  },

  /** Numbering, approvals or session: the section's fields, renamed. */
  async saveSection(section: ServerSection, values: Record<string, unknown>, reason: string) {
    return apiTransport().patch('/api/v1/organization/settings', {
      ...sectionBody(section, values),
      reason,
    });
  },

  /**
   * Several settings-row sections in one request.
   *
   * Attendance, numbering, approvals and session all live on the same row, so
   * they are saved together: one validation against the combined result, one
   * audit entry with every before-and-after, and either all of it lands or
   * none of it does. Saving them one request at a time could leave half an
   * edit applied when the second section was refused.
   */
  async saveSettings(
    sections: { attendance?: Record<string, unknown> } & Partial<
      Record<ServerSection, Record<string, unknown>>
    >,
    reason: string,
    acknowledgeTrackingPolicy: boolean,
    /** The company section, when the same save changed it: one request, one audit entry. */
    company?: Record<string, unknown>,
  ) {
    const body: Record<string, unknown> = { reason };
    if (company) body.profile = companyBody(company);
    if (sections.attendance) Object.assign(body, attendanceBody(sections.attendance));
    for (const section of Object.keys(SECTION_FIELDS) as ServerSection[]) {
      const values = sections[section];
      if (values) Object.assign(body, sectionBody(section, values));
    }
    if (acknowledgeTrackingPolicy) body.acknowledgeTrackingPolicy = true;
    return apiTransport().patch('/api/v1/organization/settings', body);
  },

  /** The idle timeout that applies to the signed-in user. */
  async sessionPolicy(): Promise<{ idleTimeoutMinutes: number; timeZone?: string }> {
    return apiTransport().get<{ idleTimeoutMinutes: number; timeZone?: string }>(
      '/api/v1/organization/session-policy',
    );
  },

  async saveAttendance(
    values: Record<string, unknown>,
    reason: string,
    acknowledgeTrackingPolicy: boolean,
  ) {
    return apiTransport().patch('/api/v1/organization/settings', {
      ...attendanceBody(values),
      reason,
      ...(acknowledgeTrackingPolicy ? { acknowledgeTrackingPolicy: true } : {}),
    });
  },
};

/** The company section, in the profile's field names. */
function companyBody(values: Record<string, unknown>) {
  return {
    legalName: values.legalName,
    displayName: values.tradeName,
    industry: values.industry || null,
    contactEmail: values.contactEmail || null,
    contactNumber: values.contactNumber || null,
    registeredAddress: values.address || null,
  };
}

/** A numbering, approvals or session section, in the server's field names. */
function sectionBody(section: ServerSection, values: Record<string, unknown>) {
  const body: Record<string, unknown> = {};
  for (const [screen, server] of Object.entries(SECTION_FIELDS[section])) {
    if (values[screen] !== undefined) body[server] = values[screen];
  }
  return body;
}

/** The attendance section, in the server's field names. */
function attendanceBody(values: Record<string, unknown>) {
  const numeric = (key: string) => {
    const value = Number(values[key]);
    return Number.isFinite(value) ? value : undefined;
  };

  return {
    graceMinutes: numeric('graceMinutes'),
    standardWorkHours: numeric('standardWorkHours'),
    requireSelfieOnTimeIn: Boolean(values.requireSelfieOnTimeIn),
    requireLocationOnTimeIn: Boolean(values.requireLocationOnTimeIn),
    requireLocationOnBreak: Boolean(values.requireLocationOnBreak),
    geofenceRadiusMeters: numeric('geofenceRadiusMeters'),
    minimumGpsAccuracyMeters: numeric('minimumGpsAccuracyMeters'),
    maximumOfflineDelayHours: numeric('maximumOfflineDelayHours'),
    continuousLocationTracking: Boolean(values.continuousLocationTracking),
    attendanceReviewThreshold: numeric('attendanceReviewThreshold'),
    attendanceRejectThreshold: numeric('attendanceRejectThreshold'),
    payslipVisibilityDays: numeric('payslipVisibilityDays'),
    attendanceSelfieRetentionDays: numeric('attendanceSelfieRetentionDays'),
    attendanceLocationRetentionDays: numeric('attendanceLocationRetentionDays'),
  };
}

/** The attendance section, in the exact keys the server validates. */
export function toAttendanceSection(
  settings: ApiOrganizationSettings,
): CompanySettings['attendance'] {
  return {
    graceMinutes: settings.graceMinutes,
    standardWorkHours: settings.standardWorkHours,
    requireSelfieOnTimeIn: settings.requireSelfieOnTimeIn,
    requireLocationOnTimeIn: settings.requireLocationOnTimeIn,
    requireLocationOnBreak: settings.requireLocationOnBreak,
    geofenceRadiusMeters: settings.geofenceRadiusMeters,
    minimumGpsAccuracyMeters: settings.minimumGpsAccuracyMeters,
    maximumOfflineDelayHours: settings.maximumOfflineDelayHours,
    continuousLocationTracking: settings.continuousLocationTracking,
    attendanceReviewThreshold: settings.attendanceReviewThreshold,
    attendanceRejectThreshold: settings.attendanceRejectThreshold,
    payslipVisibilityDays: settings.payslipVisibilityDays,
    attendanceSelfieRetentionDays: settings.attendanceSelfieRetentionDays,
    attendanceLocationRetentionDays: settings.attendanceLocationRetentionDays,
  };
}
